import { type SearchClientType } from '../types';
import { SentryError } from '@ifixit/sentry';

export interface SearchRequest {
   indexName: string;
   query: string;
   params?: Record<string, unknown>;
}

// Type guard for SearchResponse<T>
function isSearchResponse<T>(obj: unknown): obj is { hits: T[]; nbHits: number; queryID: string } {
   return (
      typeof obj === 'object' &&
      obj !== null &&
      Array.isArray((obj as { hits?: unknown[] }).hits) &&
      typeof (obj as { nbHits?: unknown }).nbHits === 'number' &&
      typeof (obj as { queryID?: unknown }).queryID === 'string'
   );
}

/**
 * Executes a batch search across multiple Algolia indices using the batch API
 *
 * @param searchClient - The initialized Algolia search client
 * @param requests - Array of search requests, each with indexName, query, and params
 */
export const multiSearchAlgolia = async <T>(
   searchClient: SearchClientType,
   requests: SearchRequest[]
): Promise<Array<{ hits: Array<T>; nbHits: number; queryID: string }>> => {
   if (!searchClient) {
      throw new SentryError('Algolia search client not set up');
   }

   if (requests.length === 0) {
      return [];
   }

   try {
      // Convert SearchRequest format to Algolia v5 format
      const algoliaRequests = requests.map(req => ({
         indexName: req.indexName,
         query: req.query,
         ...req.params,
      }));
      const response = await searchClient.search({ requests: algoliaRequests });
      const results = response.results;

      // Filter and return only valid search responses in the same order as requests
      return results.filter(isSearchResponse) as Array<{
         hits: T[];
         nbHits: number;
         queryID: string;
      }>;
   } catch (error) {
      console.error('Error during multi-search:', error);
      // Return empty results array matching the number of requests
      return requests.map(() => ({ hits: [], nbHits: 0, queryID: '' }));
   }
};
